import { ResultOf } from "@graphql-typed-document-node/core";

import { Tooltip } from "@chakra-ui/react";

import { FragmentType, getFragmentData, graphql } from "@/__generated__";
import { EnvironmentStateEnum, Maybe } from "@/__generated__/graphql";
import BadgeDot from "@/components/shared/BadgeDot";
import StateBadge from "@/components/StateBadges/StateBadge";

const EnvironmentStateBadge_Environment = graphql(`
  fragment EnvironmentStateBadge_Environment on Environment {
    state
  }
`);

function EnvironmentStateBadge(props: {
  environment?: FragmentType<typeof EnvironmentStateBadge_Environment>;
  statusReason?: string | null;
}) {
  const environment = getFragmentData(EnvironmentStateBadge_Environment, props.environment);
  if (typeof environment === "undefined") return null;

  function badgeDotForState(environment: Maybe<ResultOf<typeof EnvironmentStateBadge_Environment>>) {
    switch (environment?.state) {
      case EnvironmentStateEnum.Initialized:
      case EnvironmentStateEnum.Pausing:
      case EnvironmentStateEnum.Resuming:
      case EnvironmentStateEnum.Waiting:
        return <BadgeDot variant="yellow" />;
      case EnvironmentStateEnum.DeployingSetup:
      case EnvironmentStateEnum.DeployingPatch:
        return <BadgeDot variant="blue" />;
      case EnvironmentStateEnum.Deployed:
        return <BadgeDot variant="green" />;
      case EnvironmentStateEnum.SetupErrored:
      case EnvironmentStateEnum.PatchErrored:
      case EnvironmentStateEnum.TeardownErrored:
        return <BadgeDot variant="red" />;
      case EnvironmentStateEnum.Paused:
      case EnvironmentStateEnum.PendingRemoval:
      case EnvironmentStateEnum.Removed:
        return <BadgeDot variant="gray" />;
    }
  }

  const badge = (
    <StateBadge
      badgeDot={badgeDotForState(environment)}
      spinnerVisibleOnStates={[
        EnvironmentStateEnum.DeployingSetup,
        EnvironmentStateEnum.DeployingPatch,
        EnvironmentStateEnum.PendingRemoval,
        EnvironmentStateEnum.Pausing,
        EnvironmentStateEnum.Resuming,
      ]}
      state={environment?.state ?? "unknown"}
    />
  );

  if (props.statusReason) {
    return (
      <Tooltip hasArrow label={props.statusReason} placement="top">
        <span>{badge}</span>
      </Tooltip>
    );
  }

  return badge;
}

export default EnvironmentStateBadge;
